import { RequestDisplayStatus } from './calculateRequestDuration'

// Mirrors getRequestStatusLabel's `requests.table-column.status.<key>` English i18n values. Locale labels
// (e.g. Spanish "Pendiente") are out of scope: they resolve to no key, so the clause stays uncoerced (dropped
// on restore, or it disables the saved view) rather than being coerced by guesswork.
const STATUS_KEY_TO_LABEL: Record<RequestDisplayStatus, string> = {
  pending: 'Pending',
  completed: 'Completed',
  rejected: 'Rejected',
  paused: 'Paused',
}

// The served enum operator each accepted operator maps to. `contains`≡`equals` and `doesNotContain`≡
// `doesNotEqual` hold because the four labels are mutually distinct and none a substring of another, so a
// value naming one status matches exactly that status. `isAnyOf` is handled separately (it maps values).
const EQUIVALENT_OPERATOR: Record<string, 'equals' | 'doesNotEqual'> = {
  contains: 'equals',
  equals: 'equals',
  doesNotContain: 'doesNotEqual',
  doesNotEqual: 'doesNotEqual',
}

// The status key a value names, by full-token (not substring) case-insensitive match against the keys and
// their labels; undefined for a partial substring or a locale/unknown value, so those stay uncoerced (dropped on
// restore, or they disable the saved view) rather than being coerced.
const resolveKey = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const needle = value.trim().toLowerCase()
  const match = Object.entries(STATUS_KEY_TO_LABEL).find(
    ([key, label]) => key.toLowerCase() === needle || label.toLowerCase() === needle
  )
  return match?.[0]
}

export type FilterClause = { field: string; operator: string; value?: unknown }
export type CoercedClause = { operator: string; value: string | string[] }

// Rewrites a `status` clause to its served enum form, or returns undefined to leave it untouched (non-status
// field, an operator with no enum equivalent, or a value that names no single status).
export const coerceStatusFilterClause = (clause: FilterClause): CoercedClause | undefined => {
  if (clause.field !== 'status') return undefined

  const operator = EQUIVALENT_OPERATOR[clause.operator]
  if (operator) {
    const key = resolveKey(clause.value)
    return key === undefined ? undefined : { operator, value: key }
  }

  if (clause.operator === 'isAnyOf' && Array.isArray(clause.value)) {
    const keys: string[] = []
    for (const raw of clause.value) {
      const key = resolveKey(raw)
      if (key === undefined) return undefined
      keys.push(key)
    }
    return { operator: 'isAnyOf', value: keys }
  }

  return undefined
}
